/* Zaroori Jini OMS — Privacy Policy. */
import {
  INK, MUTED, CREAM, BORDER, Chip, Section, MarketingShell,
} from "@/components/marketing/atoms";

export default function Privacy() {
  return (
    <MarketingShell testid="privacy-page">
      <LegalHero eyebrow="Privacy" title="Privacy Policy" updated="Effective 1 Feb 2026" />
      <Section style={{ background: "#fff" }}>
        <Legal>
          <H>1. Scope</H>
          <P>
            This Privacy Policy explains how Zaroori Retail Pvt. Ltd. (&ldquo;Zaroori&rdquo;, &ldquo;we&rdquo;, &ldquo;us&rdquo;)
            collects, uses, discloses and protects information in the Zaroori Jini OMS
            platform (the &ldquo;Service&rdquo;). It applies to end-users of our Service, visitors of
            our website, and personnel of our customers.
          </P>

          <H>2. What we collect</H>
          <P>We collect only what is required to operate the Service and support our customers:</P>
          <UL items={[
            "Account information: name, work email, phone, employer, role, hashed password.",
            "Operational data: orders, SKUs, inventory, warehouses, shipments, tickets processed through the platform for the tenant that owns them.",
            "Integration credentials: OAuth tokens and API keys for marketplaces, couriers, ERPs — encrypted at rest.",
            "Product usage & telemetry: log records, IP address, device, browser, and interaction events for debugging and abuse prevention.",
            "Support communications: ticket content, attachments and consented recordings.",
          ]}/>

          <H>3. How we use information</H>
          <UL items={[
            "Provide, maintain, monitor and improve the Service.",
            "Process orders, inventory syncs, and marketplace transactions for your tenant.",
            "Detect fraud, abuse and security incidents.",
            "Respond to support requests and product notifications.",
            "Comply with applicable laws, tax and audit obligations.",
          ]}/>

          <H>4. Sharing</H>
          <P>
            We do not sell personal information. We share data with sub-processors strictly
            necessary to deliver the Service (cloud hosting, email, analytics, error
            reporting). All sub-processors are contractually bound to equivalent data
            protection standards.
          </P>

          <H>5. Data isolation & multi-tenancy</H>
          <P>
            Zaroori Jini OMS enforces strict tenant-level isolation. Data belonging to one
            customer is never accessible by another customer. Access by Zaroori personnel
            is limited to authorized staff for support and is audit-logged.
          </P>

          <H>6. Retention</H>
          <P>
            Operational data is retained for the duration of your subscription plus a legally
            mandated period (typically 8 years for tax records under Indian law).
            Backups follow a 35-day rolling window.
          </P>

          <H>7. Security</H>
          <P>
            We use encryption in transit (TLS 1.2+) and at rest, tenant isolation, immutable
            audit trails, role-based access, and least-privilege infrastructure controls.
            See our <a href="/security" style={{ color: "#DC2626", textDecoration: "none", fontWeight: 700 }}>Security page</a> for details.
          </P>

          <H>8. Your rights</H>
          <UL items={[
            "Access, correct or delete your personal information.",
            "Export your operational data (JSON/CSV) at any time.",
            "Withdraw consent for optional processing activities.",
            "Complain to a data protection authority.",
          ]}/>

          <H>9. Contact</H>
          <P>
            Data protection inquiries: <b>privacy@zaroorijini.com</b>. Registered office:
            Zaroori Retail Pvt. Ltd., Gurugram, Haryana, India.
          </P>
        </Legal>
      </Section>
    </MarketingShell>
  );
}

/* ---------- shared legal primitives (also used by Terms) ---------- */
export function LegalHero({ eyebrow, title, updated }) {
  return (
    <section
      style={{
        background: `linear-gradient(180deg, ${CREAM} 0%, #fff 100%)`,
        padding: "56px 24px 32px", borderBottom: `1px solid ${BORDER}`,
      }}
    >
      <div style={{ maxWidth: 900, margin: "0 auto", textAlign: "center" }}>
        <Chip>{eyebrow}</Chip>
        <h1
          style={{
            fontSize: "clamp(32px, 4.6vw, 52px)", fontWeight: 800, color: INK,
            marginTop: 18, letterSpacing: "-0.03em", lineHeight: 1.05,
          }}
        >
          {title}
        </h1>
        <div style={{ color: MUTED, fontSize: 13, marginTop: 12 }}>{updated}</div>
      </div>
    </section>
  );
}
export function Legal({ children }) {
  return (
    <div style={{ maxWidth: 840, margin: "0 auto", color: INK, lineHeight: 1.75 }}>
      {children}
    </div>
  );
}
export function H({ children }) {
  return (
    <h2
      style={{
        fontSize: 20, fontWeight: 800, color: INK,
        marginTop: 34, marginBottom: 10,
        letterSpacing: "-0.01em",
      }}
    >
      {children}
    </h2>
  );
}
export function P({ children }) {
  return (
    <p style={{ fontSize: 15, color: MUTED, marginTop: 8, lineHeight: 1.75 }}>
      {children}
    </p>
  );
}
export function UL({ items }) {
  return (
    <ul style={{ marginTop: 10, paddingLeft: 20 }}>
      {items.map((i) => (
        <li key={i} style={{ padding: "4px 0", color: MUTED, fontSize: 15, lineHeight: 1.7 }}>
          {i}
        </li>
      ))}
    </ul>
  );
}
